import { Form, useFormikContext } from 'formik';
import { useEffect } from 'react';

import { Button } from '../../../cdl/Button/Button';
import { FormikCheckbox } from '../../../cdl/Checkbox/FormikCheckbox';
import { DrawerContentLayout } from '../../../cdl/Drawer/DrawerContentLayout';
import { FormikInput } from '../../../cdl/Input/FormikInput';
import { Text } from '../../../cdl/Text/Text';
import { FormikTextArea } from '../../../cdl/TextArea/FormikTextArea';
import { FormikDebug } from '../../../common/form-elements/formik/FormikDebug';
import { FormikEnumMultiComboBox } from '../../../common/form-elements/formik/FormikEnumMultiComboBox';
import { FormikEnumOptionSelect } from '../../../common/form-elements/formik/FormikEnumOptionSelect';
import { FormikSupplierCompanySelect } from '../../../common/form-elements/formik/FormikSupplierCompanySelect';
import { translate } from '../../../common/helpers/translate.helper';
import { useRole } from '../../../common/hooks/useRole';
import { Box } from '../../../common/ui/Box';
import { FormikEditSupplierGroupState } from '../../../group/supplier/detail/components/EditSupplierGroupForm';

import { FormikCreateSupplierGroupState } from './CreateSupplierGroupForm';

interface SupplierGroupBaseFormProps {
    primaryActionText: string;
    showSpotAllowedInputs?: boolean;
}

export const SupplierGroupBaseForm = ({
    primaryActionText,
    showSpotAllowedInputs = false,
}: SupplierGroupBaseFormProps) => {
    const role = useRole();
    const { isSubmitting, values, setFieldValue, dirty } = useFormikContext<
        FormikEditSupplierGroupState | FormikCreateSupplierGroupState
    >();

    useEffect(() => {
        if ('spotAllowedLubes' in values && !values.spotAllowedLubes) {
            setFieldValue('spotSupplierIdLubes', '');
        }
    }, [setFieldValue, values]);

    return (
        <Form style={{ height: '100%', minHeight: 0 }}>
            <DrawerContentLayout
                isBusy={isSubmitting}
                hasUnsavedChanges={dirty}
                primaryAction={
                    <Button type="submit" emphasis="high" isLoading={isSubmitting}>
                        {primaryActionText}
                    </Button>
                }
            >
                <FormikInput name="name" label={translate('global.contact.name')} markAsRequired />
                <FormikInput name="email" label={translate('company.email')} />
                <FormikEnumOptionSelect
                    name="type"
                    enumType="supplierGroupType"
                    label={translate('suppliergroup.type')}
                    markAsRequired
                />
                <FormikInput name="phone" label={translate('company.phone')} />
                <FormikInput name="fax" label={translate('company.fax')} />

                <Text variant="subtitle1" marginTop={16}>
                    {translate('company.companyInformation')}
                </Text>

                <FormikInput
                    name="headquarter"
                    label={translate('company.headquarter')}
                    placeholder={translate('company.placeholder.headquarter')}
                />
                <FormikTextArea name="description" label={translate('company.description')} />
                <FormikInput
                    name="gtcUrl"
                    label={translate('company.gtc')}
                    placeholder={translate('company.placeholder.gtcUrl')}
                />

                <Text variant="subtitle1" marginTop={16}>
                    {translate('company.address')}
                </Text>

                <FormikInput name="address.companyName" label={translate('company.name')} />
                <Box display="grid" gridTemplateColumns="2fr 1fr" columnGap={16}>
                    <FormikInput name="address.street" label={translate('global.address.street')} />
                    <FormikInput name="address.streetNumber" label={translate('global.address.streetnumber')} />
                </Box>
                <Box display="grid" gridTemplateColumns="1fr 2fr" columnGap={16}>
                    <FormikInput name="address.zipcode" label={translate('global.address.zipcode')} />
                    <FormikInput name="address.city" label={translate('global.address.city')} />
                </Box>
                <FormikInput name="address.country" label={translate('global.address.country')} />

                {role.isSuperAdmin() ? (
                    <Box display="flex" flexDirection="column" rowGap={8}>
                        <Box display="flex" flexDirection="column" rowGap={8} alignItems="flex-start">
                            <Text variant="subtitle1" marginTop={16}>
                                {translate('company.settings')}
                            </Text>

                            <FormikCheckbox name="testCompany" label={translate('company.testCompany')} />
                            <FormikCheckbox name="emailQuote" label={translate('company.emailQuoteOnly')} />
                        </Box>

                        <FormikEnumMultiComboBox
                            name="supplierProductGroups"
                            enumType="supplierProductGroup"
                            label={translate('company.supplierproductgroups')}
                        />

                        <FormikEnumOptionSelect
                            name="translationSupplierProductGroup"
                            enumType="supplierProductGroup"
                            label={translate('company.translateproductgroup')}
                            isClearable
                        />

                        {showSpotAllowedInputs ? (
                            <Box display="flex" flexDirection="column" rowGap={8} paddingBottom="150px">
                                <FormikCheckbox name="spotAllowedLubes" label={translate('company.spotallowed')} />

                                {'spotAllowedLubes' in values && values.spotAllowedLubes ? (
                                    <FormikSupplierCompanySelect name="spotSupplierIdLubes" groupId={values.id} />
                                ) : null}
                            </Box>
                        ) : null}
                    </Box>
                ) : null}
                <FormikDebug />
                <Box paddingBottom="250px" />
            </DrawerContentLayout>
        </Form>
    );
};
